import React, {
  useMemo,
  useState,
  useEffect,
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useSelector } from "react-redux";

import { makeStyles } from "@material-ui/core/styles";

import Box from "@material-ui/core/Box";
import Select from "@material-ui/core/Select";
import Button from "@material-ui/core/Button";
import MenuItem from "@material-ui/core/MenuItem";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";

import DeleteIcon from "@material-ui/icons/Delete";

import ZonedKeyboardDatePicker from "src/components/ZonedKeyboardDatePicker";
import { getTransactions } from "src/store/transaction/selector";
import { getAllSales } from "src/store/sale/selector";
import { getEarliestInventoryYear } from "src/store/inventory/selector";
import { getConsignors } from "src/store/vendor/selector";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";
import { reportAvailableYearsSelector } from "src/store/userDataLoad/selector";
import {
  endOfCalendarDayContainingInstant,
  getDateTimePartsInTimeZone,
  startOfCalendarDayContainingInstant,
  zonedCalendarYearRange,
  zonedDateTimeToUtcDate,
  zonedMonthRangeFromZeroIndexedMonth,
  zonedQuarterRange,
  zonedTodayRange,
} from "src/utils/timezone";

import type { ReportParameters, ReportTypes } from "../report";

import { ParametersSelectionScheduleC } from "./ScheduleC";
import { useRecordEvent } from "src/hooks/useRecordEvent";

function useSalesTransactionData() {
  const sales = useSelector(getAllSales);
  const transactionState = useSelector(getTransactions);
  const transactionItems = transactionState?.items;
  const years = useSelector(reportAvailableYearsSelector);
  return useMemo(() => {
    const platforms = new Set<string>();
    if (transactionItems) {
      for (const transaction of transactionItems) {
        const platform = transaction.integration_name;
        if (platform) platforms.add(platform);
      }
    }

    if (sales) {
      for (const sale of sales) {
        const platform = sale.sale_platform;
        if (platform) platforms.add(platform);
      }
    }

    const sortedPlatforms = [...platforms];
    sortedPlatforms.sort((a: string, b: string) => a.localeCompare(b));
    return {
      years,
      platforms: sortedPlatforms,
    };
  }, [transactionItems, sales, years]);
}

const useStyles = makeStyles((theme) => ({
  keyboardDatePicker: {
    "& input": {
      width: "6em",
    },
    marginLeft: theme.spacing(1),
  },
  selectPeriod: {
    minWidth: "10em",
  },
  selectYearWrapper: {
    marginLeft: theme.spacing(1),
  },
  selectYear: {
    minWidth: "10em",
  },
  formControlPlatform: {
    marginRight: theme.spacing(1),
  },
  selectPlatform: {
    minWidth: "10em",
  },
}));

function getDefaultPeriodYear(timeZone: string): {
  DefaultPeriod: string;
  DefaultYear: string;
} {
  const { month, year } = getDateTimePartsInTimeZone(new Date(), timeZone);
  return {
    DefaultPeriod: `month:${month - 1}`,
    DefaultYear: `year:${year}`,
  };
}

/**
 * Inclusive start/end for a Period-picker value plus a calendar year in `timeZone`.
 * When `todayLiteral` is true (selecting "Today" in the Period dropdown), `calendarYear` is ignored.
 * Otherwise for "today", the range is the same month/day as the current day in the zone, in `calendarYear`
 * (Year dropdown after "Today" is selected).
 */
function zonedReportRangeForPeriodValue(
  periodValue: string,
  timeZone: string,
  options: { calendarYear: number; todayLiteral?: boolean }
): { start: Date; end: Date } | null {
  const { calendarYear, todayLiteral } = options;

  if (periodValue === "year" || periodValue === "custom") {
    return zonedCalendarYearRange(calendarYear, timeZone);
  }

  if (periodValue.startsWith("q:")) {
    const q = parseInt(periodValue.substring("q:".length), 10) as 1 | 2 | 3 | 4;
    return zonedQuarterRange(calendarYear, q, timeZone);
  }

  if (periodValue.startsWith("month:")) {
    const month0 = parseInt(periodValue.substring("month:".length), 10);
    return zonedMonthRangeFromZeroIndexedMonth(calendarYear, month0, timeZone);
  }

  if (periodValue === "today") {
    if (todayLiteral) {
      return zonedTodayRange(timeZone);
    }
    const now = new Date();
    const { month, day } = getDateTimePartsInTimeZone(now, timeZone);
    return {
      start: zonedDateTimeToUtcDate(
        {
          year: calendarYear,
          month,
          day,
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        },
        timeZone
      ),
      end: zonedDateTimeToUtcDate(
        {
          year: calendarYear,
          month,
          day,
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 999,
        },
        timeZone
      ),
    };
  }

  return null;
}

interface PeriodPickerProps {
  start?: Date | null;
  startLabel?: string;
  end?: Date | null;
  endLabel?: string;
  onChange: (
    d: { start: Date } | { end: Date } | { start: Date; end: Date }
  ) => void;
  today?: boolean;
  qsDisabled?: boolean;
  entireYearDisabled?: boolean;
  monthsDisabled?: boolean;
  startDisabled?: boolean;
  endDisabled?: boolean;
}

function PeriodPicker({
  start,
  startLabel,
  end,
  endLabel,
  children,
  onChange,
  today = false,
  qsDisabled = false,
  entireYearDisabled = false,
  monthsDisabled = false,
  startDisabled = false,
  endDisabled = false,
}: PropsWithChildren<PeriodPickerProps>) {
  const classes = useStyles();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const { DefaultPeriod, DefaultYear } = useMemo(
    () => getDefaultPeriodYear(timeZone),
    [timeZone]
  );
  const [reportPeriod, setReportPeriod] = useState(() => {
    // If months are disabled, default to current quarter
    if (monthsDisabled) {
      const month = start
        ? getDateTimePartsInTimeZone(start, timeZone).month - 1
        : getDateTimePartsInTimeZone(new Date(), timeZone).month - 1;
      const quarter = Math.floor(month / 3) + 1;
      return `q:${quarter}`;
    }
    return start
      ? `month:${getDateTimePartsInTimeZone(start, timeZone).month - 1}`
      : DefaultPeriod;
  });
  const [reportYear, setReportYear] = useState(() =>
    start
      ? `year:${getDateTimePartsInTimeZone(start, timeZone).year}`
      : DefaultYear
  );

  return (
    <>
      <FormControl variant="outlined" margin="dense">
        <InputLabel>Period</InputLabel>
        <Select
          className={classes.selectPeriod}
          margin="dense"
          label="Period"
          onChange={(e: React.ChangeEvent<any>) => {
            const value = e.target.value;
            setReportPeriod(value);

            const refDate =
              start != null
                ? new Date(start)
                : end != null
                ? new Date(end)
                : new Date();
            const refYear = getDateTimePartsInTimeZone(refDate, timeZone).year;

            if (value === "year") {
              const range = zonedReportRangeForPeriodValue("year", timeZone, {
                calendarYear: refYear,
              });
              if (range) onChange(range);
              return;
            }

            if (value.startsWith("q:") || value.startsWith("month:")) {
              const range = zonedReportRangeForPeriodValue(value, timeZone, {
                calendarYear: refYear,
              });
              if (range) onChange(range);
              return;
            }

            if (value === "today") {
              const range = zonedReportRangeForPeriodValue("today", timeZone, {
                calendarYear: refYear,
                todayLiteral: true,
              });
              if (range) {
                const y = getDateTimePartsInTimeZone(
                  range.start,
                  timeZone
                ).year;
                setReportYear(`year:${y}`);
                onChange(range);
              }
              return;
            }

            if (value === "custom") {
              setReportYear("custom");
              return;
            }
          }}
          value={reportPeriod}
        >
          {entireYearDisabled ? null : (
            <MenuItem value="year">Entire Year</MenuItem>
          )}
          {qsDisabled
            ? null
            : Array(4)
                .fill(0)
                .map((_, i) => {
                  const index = i + 1;
                  const value = `q:${index}`;
                  return (
                    <MenuItem key={value} value={value}>
                      Q{index}
                    </MenuItem>
                  );
                })}
          {monthsDisabled
            ? null
            : Array(12)
                .fill(0)
                .map((_, i) => {
                  const value = `month:${i}`;
                  const d = new Date();
                  d.setDate(1);
                  d.setMonth(i);
                  return (
                    <MenuItem key={value} value={value}>
                      {d.toLocaleDateString("en-US", { month: "short" })}
                    </MenuItem>
                  );
                })}
          {today ? (
            <MenuItem key="today" value="today">
              Today
            </MenuItem>
          ) : null}
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </FormControl>
      <FormControl
        variant="outlined"
        margin="dense"
        className={classes.selectYearWrapper}
      >
        <InputLabel>Year</InputLabel>
        <Select
          className={classes.selectYear}
          margin="dense"
          label="Year"
          onChange={(e: React.ChangeEvent<any>) => {
            const value = e.target.value;
            setReportYear(value);

            if (value.startsWith("year:")) {
              const calendarYear = parseInt(
                value.substring("year:".length),
                10
              );
              const range = zonedReportRangeForPeriodValue(
                reportPeriod,
                timeZone,
                { calendarYear }
              );
              if (range) onChange(range);
              return;
            }
          }}
          value={reportYear}
        >
          {children}
          <MenuItem value="custom">Custom</MenuItem>
        </Select>
      </FormControl>
      {startDisabled ? null : (
        <ZonedKeyboardDatePicker
          disableToolbar
          className={classes.keyboardDatePicker}
          inputVariant="outlined"
          variant="inline"
          format="MM/dd/yyyy"
          margin="dense"
          label={startLabel || "Start Date"}
          autoOk={true}
          value={start || null}
          onChange={(d) => {
            setReportPeriod("custom");
            setReportYear("custom");
            if (!d) return;
            const startDate = startOfCalendarDayContainingInstant(d, timeZone);
            onChange({ start: startDate });
          }}
          KeyboardButtonProps={{
            "aria-label": "change date",
          }}
        />
      )}
      {endDisabled ? null : (
        <ZonedKeyboardDatePicker
          disableToolbar
          className={classes.keyboardDatePicker}
          inputVariant="outlined"
          variant="inline"
          format="MM/dd/yyyy"
          margin="dense"
          label={endLabel || "End Date"}
          autoOk={true}
          value={end || null}
          onChange={(d) => {
            setReportPeriod("custom");
            setReportYear("custom");
            if (!d) return;
            const endDate = endOfCalendarDayContainingInstant(d, timeZone);
            onChange({ end: endDate });
          }}
          KeyboardButtonProps={{
            "aria-label": "change date",
          }}
        />
      )}
    </>
  );
}

interface ParametersSelectionProfitLossProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionProfitLoss({
  parameters,
  setParameters,
}: ParametersSelectionProfitLossProps) {
  const recordEvent = useRecordEvent();

  const { years } = useSalesTransactionData();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const [compareData, setCompareData] = useState(false);

  useEffect(() => {
    return () =>
      setParameters(({ compareStartDate, compareEndDate, ...rest }) => rest);
  }, [setParameters]);

  const handleCompareDataToggle = () => {
    recordEvent({
      type: "feature-adoption",
      feature: "pl-feature",
      element: "compare-data",
    });
    const newCompareData = !compareData;
    setParameters((parameters) => {
      if (!newCompareData) {
        const { compareStartDate, compareEndDate, ...rest } = parameters;
        return rest;
      }

      const now = new Date();
      const { year, month } = getDateTimePartsInTimeZone(now, timeZone);
      const { start, end } = zonedMonthRangeFromZeroIndexedMonth(
        year,
        month - 1,
        timeZone
      );

      return {
        ...parameters,
        compareStartDate: start,
        compareEndDate: end,
      };
    });
    setCompareData(newCompareData);
  };

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1} id={"report-time-selection"}>
        <>
          <PeriodPicker
            today
            start={parameters.startDate}
            end={parameters.endDate}
            onChange={(value) => {
              setParameters((current) =>
                Object.entries(value).reduce(
                  (p, [k, v]) => {
                    p[k === "start" ? "startDate" : "endDate"] = v;
                    return p;
                  },
                  { ...current }
                )
              );
            }}
          >
            {yearItems}
          </PeriodPicker>
          <Box>
            <Button onClick={handleCompareDataToggle}>Compare Data</Button>
          </Box>
          {compareData && (
            <Box display="flex" alignItems="center">
              <PeriodPicker
                today
                start={parameters.compareStartDate}
                end={parameters.compareEndDate}
                onChange={(value) => {
                  setParameters((current) =>
                    Object.entries(value).reduce(
                      (p, [k, v]) => {
                        p[
                          k === "start" ? "compareStartDate" : "compareEndDate"
                        ] = v;
                        return p;
                      },
                      { ...current }
                    )
                  );
                }}
              >
                {yearItems}
              </PeriodPicker>
              <IconButton onClick={handleCompareDataToggle}>
                <DeleteIcon />
              </IconButton>
            </Box>
          )}
        </>
      </Box>
      {!compareData && (
        <Box height={52} display="flex" alignItems="center">
          <Typography variant="body1">
            A profit and loss statement is an accounting report that tells you
            about your income and expenses over a period of time.
          </Typography>
        </Box>
      )}
    </>
  );
}

interface ParametersSelectionProfitLossComparisonProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionProfitLossComparison({
  parameters,
  setParameters,
}: ParametersSelectionProfitLossComparisonProps) {
  const { years } = useSalesTransactionData();
  const timeZone = useSelector(effectiveUserTimeZoneSelector);

  // Set default to current quarter on mount
  useEffect(() => {
    const now = new Date();
    const { year, month } = getDateTimePartsInTimeZone(now, timeZone);
    const quarter = (Math.floor((month - 1) / 3) + 1) as 1 | 2 | 3 | 4;
    const { start: startDate, end: endDate } = zonedQuarterRange(
      year,
      quarter,
      timeZone
    );

    setParameters((current) => ({
      ...current,
      startDate,
      endDate,
    }));
  }, [setParameters, timeZone]);

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1}>
        <PeriodPicker
          monthsDisabled
          start={parameters.startDate}
          end={parameters.endDate}
          onChange={(value) => {
            setParameters((current) =>
              Object.entries(value).reduce(
                (p, [k, v]) => {
                  p[k === "start" ? "startDate" : "endDate"] = v;
                  return p;
                },
                { ...current }
              )
            );
          }}
        >
          {yearItems}
        </PeriodPicker>
      </Box>
      <Box height={52} display="flex" alignItems="center">
        <Typography variant="body1">
          Compare your profit and loss across multiple months or quarters within
          the selected date range.
        </Typography>
      </Box>
    </>
  );
}

interface ParametersSelectionSalesTaxProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionSalesTax({
  parameters,
  setParameters,
}: ParametersSelectionSalesTaxProps) {
  const { years } = useSalesTransactionData();

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1} mb={1}>
        <PeriodPicker
          today
          start={parameters.startDate}
          end={parameters.endDate}
          onChange={(value) => {
            setParameters((current) =>
              Object.entries(value).reduce(
                (p, [k, v]) => {
                  p[k === "start" ? "startDate" : "endDate"] = v;
                  return p;
                },
                { ...current }
              )
            );
          }}
        >
          {yearItems}
        </PeriodPicker>
      </Box>
      <Typography variant="body1">
        A summary of sales tax collected by state and broken out by the ‘Liable
        to Pay’ field
      </Typography>
    </>
  );
}

function ParametersSelectionInventory({ parameters, setParameters }) {
  const reportYears = useSelector(reportAvailableYearsSelector);
  const earliestInventoryYear = useSelector(getEarliestInventoryYear);
  const timeZone = useSelector(effectiveUserTimeZoneSelector);
  const currentYear = getDateTimePartsInTimeZone(new Date(), timeZone).year;
  const allInventoryYears = useMemo(() => {
    const years = new Set<number>(reportYears);
    for (let year = earliestInventoryYear; year <= currentYear; year += 1) {
      years.add(year);
    }
    const sortedYears = [...years].sort((a, b) => a - b);
    return sortedYears.map((year) => (
      <MenuItem key={year} value={"" + year}>
        {year}
      </MenuItem>
    ));
  }, [currentYear, earliestInventoryYear, reportYears]);

  return (
    <>
      <Box display="flex" my={1}>
        <FormControl variant="outlined" margin="dense">
          <InputLabel id="year">Select Year</InputLabel>
          <Select
            label="Select Year"
            margin="dense"
            onChange={(e: any) => {
              setParameters((current) => ({
                ...current,
                year: e.target.value,
              }));
            }}
            value={parameters.year || currentYear}
          >
            {allInventoryYears}
          </Select>
        </FormControl>
      </Box>
      <Typography variant="body1">
        The inventory report gives you the inventory information used to
        calculate your cost of goods sold for a given year. You'll give this
        info to your accountant or enter these details on your tax forms.
      </Typography>
    </>
  );
}

interface ParametersSelectionPlatformTransactionProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionPlatformTransaction({
  parameters,
  setParameters,
}: ParametersSelectionPlatformTransactionProps) {
  const classes = useStyles();
  const { years, platforms } = useSalesTransactionData();

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1}>
        <FormControl
          variant="outlined"
          margin="dense"
          className={classes.formControlPlatform}
        >
          <InputLabel>Platform</InputLabel>
          <Select
            disabled={!platforms.length}
            margin="dense"
            label="Platform"
            className={classes.selectPlatform}
            value={parameters.platform || ""}
            onChange={(e: React.ChangeEvent<any>) => {
              const platform = e.target.value;
              setParameters((p) => ({ ...p, platform }));
            }}
          >
            {platforms.map((platform) => (
              <MenuItem key={platform} value={platform}>
                {platform}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <PeriodPicker
          today
          start={parameters.startDate}
          end={parameters.endDate}
          onChange={(value) => {
            setParameters((current) =>
              Object.entries(value).reduce(
                (p, [k, v]) => {
                  p[k === "start" ? "startDate" : "endDate"] = v;
                  return p;
                },
                { ...current }
              )
            );
          }}
        >
          {yearItems}
        </PeriodPicker>
      </Box>
      <Box display="flex" alignItems="center" mt={1}>
        <Typography variant="body1">
          A platform transaction report shows a list of all the transactions for
          the platform you select during the time period you select -- this
          includes sales, returns/refunds/cancellations and expenses.
        </Typography>
      </Box>
    </>
  );
}

interface ParametersSelectionConsignmentCommissionProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionConsignmentCommission({
  parameters,
  setParameters,
}: ParametersSelectionConsignmentCommissionProps) {
  const classes = useStyles();
  const { years } = useSalesTransactionData();
  const consignors = useSelector(getConsignors);

  useEffect(() => {
    return () =>
      setParameters(({ compareStartDate, compareEndDate, ...rest }) => rest);
  }, [setParameters]);

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1}>
        <FormControl
          variant="outlined"
          margin="dense"
          className={classes.formControlPlatform}
        >
          <InputLabel>Consignor</InputLabel>
          <Select
            disabled={!consignors.length}
            margin="dense"
            label="Consignor"
            className={classes.selectPlatform}
            value={parameters.consignor?.id || ""}
            onChange={(e: React.ChangeEvent<any>) => {
              const id = e.target.value;
              setParameters((p) => ({
                ...p,
                consignor: consignors.find((c) => c.id === id),
              }));
            }}
          >
            {consignors.map((consignor) => (
              <MenuItem key={consignor.id} value={consignor.id}>
                {consignor.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <PeriodPicker
          today
          start={parameters.startDate}
          end={parameters.endDate}
          onChange={(value) => {
            setParameters((current) =>
              Object.entries(value).reduce(
                (p, [k, v]) => {
                  p[k === "start" ? "startDate" : "endDate"] = v;
                  return p;
                },
                { ...current }
              )
            );
          }}
        >
          {yearItems}
        </PeriodPicker>
      </Box>
      <Box display="flex" alignItems="center" mt={1}>
        <Typography variant="body1">
          A consignment commission report will allow you to select a consignor
          (Vendors marked as consignors) and view the sales for a given time
          period that are tied to that consignor. It will show a summary of
          sales, fees, and calculated commission owed to the consignor.
        </Typography>
      </Box>
    </>
  );
}

interface ParametersSelectionBalanceSheetProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
}

function ParametersSelectionBalanceSheet({
  parameters,
  setParameters,
}: ParametersSelectionBalanceSheetProps) {
  const { years } = useSalesTransactionData();

  const yearItems = years.map((year) => {
    const key = `year:${year}`;
    return (
      <MenuItem key={key} value={key}>
        {year}
      </MenuItem>
    );
  });

  return (
    <>
      <Box mt={1} mb={1}>
        <PeriodPicker
          today
          entireYearDisabled
          qsDisabled
          startDisabled
          endLabel="As of date"
          end={parameters.date}
          onChange={(value) => {
            if (!("end" in value)) return;
            const date = value.end;
            if (!date) return;
            setParameters((current) => ({
              ...current,
              date,
            }));
          }}
        >
          {yearItems}
        </PeriodPicker>
      </Box>
      <Typography variant="body1">
        A balance sheet gives you a snapshot of your company assets,
        liabilities, and equity at a given point in time.
      </Typography>
    </>
  );
}

export interface ParametersSelectionProps {
  parameters: ReportParameters;
  setParameters: Dispatch<SetStateAction<ReportParameters>>;
  reportType: ReportTypes;
}

function ParametersSelection({
  parameters,
  setParameters,
  reportType,
}: ParametersSelectionProps) {
  switch (reportType) {
    case "inventory":
      return (
        <ParametersSelectionInventory
          parameters={parameters}
          setParameters={setParameters}
        />
      );
    case "profit/loss":
      return (
        <ParametersSelectionProfitLoss
          parameters={parameters}
          setParameters={setParameters}
        />
      );

    case "profit/loss-comparison":
      return (
        <ParametersSelectionProfitLossComparison
          parameters={parameters}
          setParameters={setParameters}
        />
      );

    case "platform-transaction":
      return (
        <ParametersSelectionPlatformTransaction
          parameters={parameters}
          setParameters={setParameters}
        />
      );
    case "consignment-commission":
      return (
        <ParametersSelectionConsignmentCommission
          parameters={parameters}
          setParameters={setParameters}
        />
      );

    case "sales-tax":
      return (
        <ParametersSelectionSalesTax
          parameters={parameters}
          setParameters={setParameters}
        />
      );

    case "schedule-c":
      return (
        <ParametersSelectionScheduleC
          parameters={parameters}
          setParameters={setParameters}
        />
      );

    case "balance-sheet":
      return (
        <ParametersSelectionBalanceSheet
          parameters={parameters}
          setParameters={setParameters}
        />
      );
  }

  return null;
}

export default ParametersSelection;
